import { ReactElement } from 'react';
import { GroupBase, mergeStyles, StylesConfig } from 'react-select';
import LibraryAsyncSelect, { AsyncProps } from 'react-select/async';
import { CommonSelectProps, ValueLabelOption } from '../types';
import { Label } from '../../Label';
import { SelectSize } from '../enums';
import { prefixMenuOptionsWithCheckIcon } from '../formatting';
import { joinClassNames } from '../../../../utils';
import { getDefaultMultiStyles, getDefaultSingleStyles } from '../defaultStyles';
import formStyles from '../../styles.module.scss';

export type AsyncSelectCustomProps = CommonSelectProps;

export type AsyncSelectProps<
  Option extends ValueLabelOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<AsyncProps<Option, IsMulti, Group>, 'id'> & AsyncSelectCustomProps;

export function AsyncSelect<
  Option extends ValueLabelOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  id,
  label,
  hideLabel = false,
  isRequired = false,
  size = SelectSize.Large,
  formatOptionLabel,
  isMulti,
  styles: customStyles,
  ...reactSelectProps
}: AsyncSelectProps<Option, IsMulti, Group>): ReactElement {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const defaultStyles: StylesConfig<Option, IsMulti, Group> = isMulti
    ? getDefaultMultiStyles<Option>(size)
    : getDefaultSingleStyles<Option>(size);

  const inputId = `${id}-input`;

  return (
    <>
      <Label htmlFor={inputId} hidden={hideLabel} isRequired={isRequired}>
        {label}
      </Label>
      <LibraryAsyncSelect<Option, IsMulti, Group>
        id={id}
        inputId={inputId}
        className={joinClassNames(formStyles.select, !hideLabel && formStyles.hasLabel)}
        classNamePrefix="fecReactSelect"
        formatOptionLabel={formatOptionLabel || (!isMulti ? prefixMenuOptionsWithCheckIcon() : undefined)}
        isMulti={isMulti}
        styles={mergeStyles(defaultStyles, customStyles)}
        {...reactSelectProps}
      />
    </>
  );
}
